import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Circle, Trophy, Mail, Plus, Trash2, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useJourney } from "@/v2/V2Shell";
import { JourneyPath } from "@/v2/JourneyPath";
import { YearArc } from "@/v2/YearArc";
import { VisionStudio, PillarSliders, PILLARS } from "@/v2/Vision";
import { YearMap } from "@/v2/YearMap";
import Trends from "@/pages/Trends";
import Reflections from "@/pages/Reflections";

const PACES = [["gentle", "Gentle"], ["steady", "Steady"], ["ambitious", "Ambitious"]];
const STATUS = [["active", "Active"], ["maintenance", "Maintenance"], ["paused", "Pause"]];

function PhaseCard() {
  const { s, setS } = useJourney();
  const [letter, setLetter] = useState(s.journey.letter_to_next || "");
  const r = s.readiness, i = s.journey.phase_index, last = i === 5;
  const go = async (action) => {
    try { const { data } = await api.post("/v2/journey/advance", { action }); setS(data); toast.success(action === "advance" ? `Welcome to ${data.phase.name}. Your chapter letter is being written.` : `Back to ${data.phase.name}. A smart move.`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const nextYear = async () => { try { const { data } = await api.post("/v2/journey/next-year"); setS(data); toast.success(`Year ${data.journey.year} begins.`); } catch (e) { toast.error(errMsg(e)); } };
  const saveLetter = async () => { const { data } = await api.patch("/v2/journey", { letter_to_next: letter }); setS(data); toast.success("Saved for next-year you"); };
  return (
    <section className="card p-5 sm:p-6" data-testid="phase-card">
      <div className="eyebrow" style={{ color: s.phase.color }}>Current chapter · {i + 1} of 6 · {s.phase.season}</div>
      <h2 className="font-display text-3xl mt-2"><span style={{ color: s.phase.color }}>{s.phase.letter}</span> · {s.phase.name}</h2>
      <p className="font-display italic text-stone-700 mt-1">{s.phase.tagline}</p>
      <p className="text-stone-600 text-sm mt-1">{s.phase.focus}</p>
      <p className="text-xs text-stone-500 mt-3">{r.weeks_in_phase} week{r.weeks_in_phase === 1 ? "" : "s"} in this phase · suggested {s.phase.weeks} · move on from week {r.min_weeks} when ready</p>
      <h3 className="eyebrow mt-6" data-testid="phase-next-steps">{last ? "To close your year" : r.ready ? "You've done what this chapter asks" : "Next steps for this chapter"}</h3>
      <ul className="mt-3 space-y-2" data-testid="phase-markers">
        {r.markers.map((m) => <li key={m.text} className="flex items-center gap-2.5 text-sm">{m.met ? <Check className="h-4 w-4" style={{ color: s.phase.color }} /> : <Circle className="h-4 w-4 text-stone-300" />}<span className={m.met ? "" : "text-stone-500"}>{m.text}</span></li>)}
      </ul>
      {last && (
        <div className="mt-5">
          <label className="text-sm font-semibold">A letter to next-year you</label>
          <textarea value={letter} onChange={(e) => setLetter(e.target.value)} rows={3} data-testid="letter-to-next" className="mt-1 w-full rounded-xl border border-stone-200 p-3 text-sm" />
          <button onClick={saveLetter} data-testid="letter-save-btn" className="text-xs underline mt-1">Save letter</button>
        </div>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {last ? (
          <button onClick={nextYear} data-testid="next-year-btn" className="btn-ink px-5 h-11 rounded-full text-sm font-semibold">Begin your next year</button>
        ) : r.ready ? (
          <>
            <button onClick={() => go("advance")} data-testid="phase-advance-btn" className="btn-ink px-5 h-11 rounded-full text-sm font-semibold">You're ready. Move to {s.next_phase?.name}</button>
            <span className="text-xs text-stone-500">or stay as long as you like</span>
          </>
        ) : (
          <button onClick={() => go("advance")} data-testid="phase-advance-anyway" className="h-11 inline-flex items-center text-xs underline text-stone-600">I feel ready, move on anyway</button>
        )}
        {i > 0 && <button onClick={() => go("back")} data-testid="phase-back-btn" className="h-11 inline-flex items-center text-xs underline text-stone-500">Step back a phase</button>}
      </div>
    </section>
  );
}

function PaceStatus() {
  const { s, setS } = useJourney();
  const patch = async (b) => { const { data } = await api.patch("/v2/journey", b); setS(data); };
  const status = async (st) => { const { data } = await api.post("/v2/journey/status", { status: st }); setS(data); toast.success(st === "active" ? "Your year clock is running" : "Your year clock is paused. Nothing is lost."); };
  const Seg = ({ opts, val, on, id }) => (
    <div className="inline-flex p-1 rounded-full bg-[#EFECE6]">{opts.map(([k, l]) => <button key={k} onClick={() => on(k)} data-testid={`${id}-${k}`} className={`chip min-h-11 px-3.5 rounded-full text-sm ${val === k ? "bg-white shadow-sm font-semibold" : "text-stone-500"}`}>{l}</button>)}</div>
  );
  return (
    <section className="card p-5 space-y-4" data-testid="pace-status">
      <div className="flex flex-wrap items-center justify-between gap-3"><div><div className="font-medium">Pace</div><div className="text-xs text-stone-500">How hard each day pushes</div></div><Seg opts={PACES} val={s.journey.pace} on={(k) => patch({ pace: k })} id="pace" /></div>
      <div className="flex flex-wrap items-center justify-between gap-3"><div><div className="font-medium">Right now</div><div className="text-xs text-stone-500">Pause or maintenance stops the clock</div></div><Seg opts={STATUS} val={s.journey.status} on={status} id="status" /></div>
    </section>
  );
}

function Pillars() {
  const { s, setS } = useJourney();
  const [open, setOpen] = useState(false);
  const base = s.journey.baseline || {};
  const latest = (s.journey.ratings || []).slice(-1)[0]?.values || base;
  const [vals, setVals] = useState(latest);
  const save = async () => { const { data } = await api.post("/v2/journey/ratings", { values: vals }); setS(data); setOpen(false); toast.success("Saved. Look how far you've come."); };
  return (
    <section className="card p-5" data-testid="pillars-card">
      <div className="flex items-center justify-between mb-4"><span className="eyebrow">Then vs now</span>
        <button onClick={() => setOpen(true)} data-testid="rerate-btn" className={`min-h-11 text-xs font-semibold px-4 rounded-full ${s.needs_rating ? "btn-ink" : "border border-stone-300"}`}>{s.needs_rating ? "Monthly re-rate due" : "Re-rate"}</button></div>
      <div className="space-y-3">
        {PILLARS.map(([k, l]) => (
          <div key={k} data-testid={`pillar-${k}`}>
            <div className="flex justify-between text-sm"><span>{l}</span><span className="text-stone-500">{base[k] ?? "–"} → <b className="text-stone-900">{latest[k] ?? "–"}</b>/10</span></div>
            <div className="relative h-2 rounded-full bg-[#EFECE6] mt-1.5">
              <div className="absolute inset-y-0 left-0 rounded-full bg-stone-300" style={{ width: `${(base[k] || 0) * 10}%` }} />
              <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(latest[k] || 0) * 10}%`, background: s.phase.color, opacity: 0.85 }} />
            </div>
            {s.journey.vision?.[k] && <p className="text-xs text-stone-500 mt-1 italic">"{s.journey.vision[k]}"</p>}
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="rerate-dialog"><DialogHeader><DialogTitle className="font-display">How do you feel about each part of your life now?</DialogTitle></DialogHeader>
          <PillarSliders values={vals} setValues={setVals} /><button onClick={save} data-testid="rerate-save" className="btn-ink h-11 rounded-full font-semibold mt-2">Save ratings</button></DialogContent>
      </Dialog>
    </section>
  );
}

function VisionCard() {
  const { s, setS } = useJourney();
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(s.journey.vision || {});
  const save = async () => { const { data } = await api.patch("/v2/journey", { vision: v }); setS(data); setOpen(false); toast.success("Vision updated"); };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="vision-edit-btn" className="card p-5 w-full text-left chip hover:border-stone-300">
        <span className="eyebrow">Your year-end vision</span>
        <p className="font-display text-lg mt-2 leading-snug">{s.journey.why || s.journey.vision?.health || "Write your vision"}</p>
        <span className="text-xs underline text-stone-500 mt-2 inline-block">Reshape it with CAMRYN</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="vision-dialog"><DialogHeader><DialogTitle className="font-display">Vision Studio</DialogTitle></DialogHeader>
          <VisionStudio vision={v} setVision={setV} /><button onClick={save} data-testid="vision-save" className="btn-ink h-11 rounded-full font-semibold">Save my vision</button></DialogContent>
      </Dialog>
    </>
  );
}

function Wins() {
  const { s, reload } = useJourney();
  const [text, setText] = useState("");
  const add = async (e) => { e.preventDefault(); if (!text.trim()) return; await api.post("/v2/wins", { text }); setText(""); reload(); toast.success("Win saved"); };
  const del = async (id) => { await api.delete(`/v2/wins/${id}`); reload(); };
  return (
    <section className="card p-5" data-testid="wins-card">
      <span className="eyebrow flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" />Evidence of change</span>
      <form onSubmit={add} className="flex gap-2 mt-3"><input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} placeholder="Add a win, big or small" data-testid="win-input" className="flex-1 min-w-0 h-11 px-3 rounded-lg border border-stone-200 text-sm" />
        <button data-testid="win-add-btn" aria-label="Add win" className="btn-ink h-11 w-11 shrink-0 rounded-full grid place-items-center"><Plus className="h-4 w-4" /></button></form>
      <ol className="mt-4 relative border-l border-stone-200 ml-1.5 space-y-3.5">
        {s.wins.map((w) => (
          <li key={w.id} className="pl-4 pr-12 relative group min-h-11" data-testid="win-item">
            <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full" style={{ background: s.phase.color }} />
            <div className="text-sm">{w.text}</div><div className="text-[11px] text-stone-400">{new Date(w.date + "T00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })}{w.source === "auto" ? " · noticed by CAMRYN" : ""}</div>
            <button onClick={() => window.confirm("Delete this win?") && del(w.id)} aria-label="Delete win" data-testid="win-delete" className="absolute right-0 top-0 h-11 w-11 grid place-items-center rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
        {!s.wins.length && <li className="pl-4 text-sm text-stone-500">Your wins will collect here: real things, not points.</li>}
      </ol>
    </section>
  );
}

function Letters() {
  const [letters, setLetters] = useState([]);
  useEffect(() => { api.get("/v2/letters").then(({ data }) => setLetters(data)).catch(() => {}); }, []);
  if (!letters.length) return null;
  return (
    <section className="space-y-3" data-testid="letters">
      <span className="eyebrow flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Chapter letters</span>
      {letters.map((l) => (
        <article key={l.id} className="card p-6 bg-[#FFFDF8]" data-testid="letter-item">
          <div className="eyebrow">End of {l.phase_name} · Year {l.year}</div>
          <p className="font-display text-[17px] leading-relaxed mt-3 whitespace-pre-line text-stone-800">{l.text}</p>
        </article>
      ))}
    </section>
  );
}

export function JourneyProgress() {
  const { s } = useJourney();
  const weight = s.projects.some((p) => p.key === "weight_reset" && p.status === "active");
  return (
    <div className="grid gap-6 lg:grid-cols-3 items-start" data-testid="journey-progress">
      <section className="card p-5 sm:p-6 min-w-0 lg:col-span-2 lg:col-start-1 lg:row-start-1"><Trends embedded /></section>
      <div className="space-y-4 min-w-0 lg:col-start-3 lg:row-start-1">
        <Pillars />
        {weight && <Link to="/journey/plan/weight" data-testid="progress-weight-link" className="card p-5 flex items-center justify-between gap-3 chip hover:border-stone-300"><span><span className="block font-semibold">Weight Reset</span><span className="block text-xs text-stone-500">Trend line, weekly targets and your honest weekly verdict</span></span><span className="text-sm underline shrink-0">Open</span></Link>}
      </div>
    </div>
  );
}

export function JourneyStory() {
  const { s } = useJourney();
  return (
    <div className="grid gap-6 lg:grid-cols-3 items-start" data-testid="journey-story">
      <div className="space-y-4 min-w-0 lg:col-span-2">
        <Letters />
        <section className="card p-5 sm:p-6"><Reflections embedded /></section>
      </div>
      <div className="space-y-4 min-w-0">
        <Wins />
        {s.years.length > 0 && <section className="card p-5" data-testid="past-years"><span className="eyebrow">Past years</span>{s.years.map((y) => <p key={y.year} className="text-sm mt-2">Year {y.year}: {y.weeks} weeks, {y.started_at} to {y.ended_at}</p>)}</section>}
      </div>
    </div>
  );
}

export function JourneyYear() {
  const { s } = useJourney();
  return (
    <div className="grid gap-x-6 gap-y-4 lg:grid-cols-3 items-start" data-testid="v2-journey">
      <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1 min-w-0 rise space-y-4"><JourneyPath /><PhaseCard /></div>
      <aside className="space-y-4 min-w-0 lg:col-start-3 lg:row-start-1 lg:row-span-4">
      <section className="card p-4 sm:p-5 rise d1" data-testid="journey-overview">
        <span className="eyebrow">Year at a glance</span>
        <div className="mx-auto max-w-[320px] mt-2"><YearArc arc={s.arc} total={s.total_weeks} week={s.week} year={s.journey.year} status={s.journey.status} compact /></div>
      </section>
      <Link to="/recap" data-testid="journey-recap-link" className="card p-4 sm:p-5 flex items-center gap-4 bg-[#2D2926] border-[#2D2926] text-stone-100 chip rise d2">
        <span className="h-11 w-11 rounded-full bg-[#E6B057] text-[#2D2926] grid place-items-center"><Play className="h-5 w-5 ml-0.5" /></span>
        <span className="flex-1">
          <span className="block font-display text-lg">{s.journey.phase_index === 5 || s.week >= 52 ? "Your year-end reveal is ready" : "Play your year so far"}</span>
          <span className="block text-xs text-stone-300">{s.journey.phase_index === 5 || s.week >= 52 ? "Then vs now, every win and letter, and what comes next" : `Weeks 1–${s.week}: then vs now, wins and letters. The full reveal unlocks at week 52.`}</span>
        </span>
      </Link>
      </aside>
      <div className="lg:col-span-2 lg:col-start-1 min-w-0"><VisionCard /></div>
      <div className="lg:col-span-2 lg:col-start-1 min-w-0"><PaceStatus /></div>
      <div className="lg:col-span-2 lg:col-start-1 min-w-0"><YearMap /></div>
    </div>
  );
}
